import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from 'framer-motion';
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User, ArrowLeft, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    accept_terms: false,
    accept_privacy: false,
    accept_widerruf: false,
  });
  const navigate = useNavigate();

  const resetMode = (toLogin) => {
    setIsLogin(toLogin);
    setRegistrationSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });
        localStorage.setItem("access_token", response.data.access_token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        toast.success("Erfolgreich eingeloggt!");
        navigate("/app");
      } else {
        // Registration with DSGVO checkboxes
        if (!formData.accept_terms || !formData.accept_privacy || !formData.accept_widerruf) {
          toast.error("Bitte akzeptieren Sie alle Pflichtangaben");
          setIsLoading(false);
          return;
        }
        await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          accept_terms: formData.accept_terms,
          accept_privacy: formData.accept_privacy,
          accept_widerruf: formData.accept_widerruf,
        });
        setRegisteredEmail(formData.email);
        setRegistrationSuccess(true);
        toast.success("Registrierung erfolgreich! Bitte E-Mail bestätigen.");
      }
    } catch (error) {
      console.error("Auth error:", error);
      const message = error.response?.data?.detail || "Ein Fehler ist aufgetreten";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: registeredEmail });
      toast.success("Bestätigungslink erneut versendet.");
    } catch (e) {
      toast.error("Konnte E-Mail nicht erneut senden.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2835F7] via-[#1a25c9] to-[#0d1570] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-1/2 -left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-1/2 -right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <Link
          to="/"
          className="absolute top-8 left-8 flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-200"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Zurück zur Startseite</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
            <div className="flex justify-center mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_german-bizleads/artifacts/aplit9ht_1.png"
                alt="Adloc Logo"
                className="h-20 w-auto"
              />
            </div>

            {registrationSuccess ? (
              <div className="text-center text-white" data-testid="registration-success-screen">
                <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-400" />
                <h2 className="text-2xl font-bold mb-3">Registrierung erfolgreich!</h2>
                <p className="text-white/80 mb-2">Wir haben einen Bestätigungslink an</p>
                <p className="font-semibold mb-3">{registeredEmail}</p>
                <p className="text-white/80 text-sm mb-6">
                  gesendet. Bitte klicken Sie auf den Link, um Ihren Account zu aktivieren und Ihre 10 kostenlosen Test-Credits (14 Tage gültig) zu erhalten.
                </p>
                <Button
                  onClick={handleResend}
                  disabled={isLoading}
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 mb-3 w-full"
                  data-testid="resend-verification-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Bestätigungslink erneut senden"}
                </Button>
                <button
                  onClick={() => resetMode(true)}
                  className="text-white/90 hover:text-white text-sm underline"
                  data-testid="goto-login-from-success"
                >
                  Zurück zum Login
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h1 className="text-3xl font-bold text-white mb-2">
                    {isLogin ? "Willkommen zurück!" : "Konto erstellen"}
                  </h1>
                  <p className="text-white/80">
                    {isLogin ? "Melden Sie sich in Ihrem Konto an" : "Starten Sie mit 10 kostenlosen Credits"}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid={isLogin ? "login-form" : "register-form"}>
                  {!isLogin && (
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white font-semibold">Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
                        <Input
                          id="name"
                          data-testid="register-name-input"
                          type="text"
                          placeholder="Ihr Name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required={!isLogin}
                          className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white focus:ring-white/30 pl-10 rounded-xl"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white font-semibold">E-Mail</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
                      <Input
                        id="email"
                        data-testid="auth-email-input"
                        type="email"
                        placeholder="ihre@email.de"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white focus:ring-white/30 pl-10 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-white font-semibold">Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
                      <Input
                        id="password"
                        data-testid="auth-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={isLogin ? undefined : 8}
                        className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-white focus:ring-white/30 pl-10 rounded-xl"
                      />
                    </div>
                    {!isLogin && (
                      <p className="text-xs text-white/60">Mindestens 8 Zeichen</p>
                    )}
                  </div>

                  {!isLogin && (
                    <div className="space-y-3 pt-2 text-sm">
                      <label className="flex items-start gap-2 cursor-pointer text-white/90">
                        <Checkbox
                          checked={formData.accept_terms}
                          onCheckedChange={(checked) => setFormData({ ...formData, accept_terms: !!checked })}
                          data-testid="accept-terms-checkbox"
                          className="mt-0.5 border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-[#2835F7]"
                        />
                        <span>
                          Ich akzeptiere die{" "}
                          <Link to="/impressum" target="_blank" className="underline">AGB</Link>.
                        </span>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer text-white/90">
                        <Checkbox
                          checked={formData.accept_privacy}
                          onCheckedChange={(checked) => setFormData({ ...formData, accept_privacy: !!checked })}
                          data-testid="accept-privacy-checkbox"
                          className="mt-0.5 border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-[#2835F7]"
                        />
                        <span>
                          Ich habe die{" "}
                          <Link to="/datenschutz" target="_blank" className="underline">Datenschutzerklärung</Link>{" "}
                          gelesen und akzeptiert (DSGVO).
                        </span>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer text-white/90">
                        <Checkbox
                          checked={formData.accept_widerruf}
                          onCheckedChange={(checked) => setFormData({ ...formData, accept_widerruf: !!checked })}
                          data-testid="accept-widerruf-checkbox"
                          className="mt-0.5 border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-[#2835F7]"
                        />
                        <span>
                          Ich nehme die <strong>Widerrufsbelehrung</strong> zur Kenntnis. Mit der Aktivierung beginnt der Service sofort, wodurch das Widerrufsrecht erlischt sobald Credits verbraucht werden.
                        </span>
                      </label>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid={isLogin ? "login-submit-btn" : "register-submit-btn"}
                    className="w-full h-12 bg-white hover:bg-gray-100 text-[#2835F7] font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    {isLoading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Bitte warten...</>
                    ) : (
                      isLogin ? "Anmelden" : "Registrieren"
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => resetMode(!isLogin)}
                    data-testid="toggle-auth-mode"
                    className="text-white/90 hover:text-white font-medium transition-colors duration-200"
                  >
                    {isLogin ? (
                      <>Noch kein Account? <span className="underline">Jetzt registrieren →</span></>
                    ) : (
                      <>Bereits registriert? <span className="underline">Jetzt anmelden →</span></>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          {!isLogin && !registrationSuccess && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6 text-center"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-lg px-4 py-2 rounded-full">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-white text-sm font-medium">10 Test-Credits · 14 Tage gültig</span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
